import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nombre, setNombre] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [fotoPerfil, setFotoPerfil] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register(email, password, nombre, fechaNacimiento, fotoPerfil);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoPerfil(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-dark-mk flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_espanol-mode-15/artifacts/jlomgnz6_WhatsApp%20Image%202025-11-16%20at%2010.07.15.jpeg"
            alt="Mallorca Klan Logo"
            className="logo-mk mx-auto mb-4"
            data-testid="logo-mallorca-klan"
          />
          <h1 className="text-3xl font-bold text-gold">Mallorca Klan</h1>
          <p className="text-gold text-lg italic mt-1 font-medium">Aquí empieza el cambio</p>
          <p className="text-gray-400 mt-2">Sistema de Gestión de Entrenamientos</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-gray-900">
            <TabsTrigger value="login" data-testid="tab-login">Iniciar Sesión</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register">Registrarse</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Bienvenido</CardTitle>
                <CardDescription className="text-gray-400">
                  Ingresa tus credenciales para continuar
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-white">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-white">Contraseña</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="login-password-input"
                    />
                  </div>
                  {error && (
                    <div className="text-red-500 text-sm" data-testid="error-message">{error}</div>
                  )}
                  <Button
                    type="submit"
                    className="w-full btn-primary-mk"
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    {loading ? 'Cargando...' : 'Iniciar Sesión'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Crear Cuenta</CardTitle>
                <CardDescription className="text-gray-400">
                  Únete a la comunidad Mallorca Klan
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-nombre" className="text-white">Nombre</Label>
                    <Input
                      id="register-nombre"
                      type="text"
                      placeholder="Tu nombre"
                      value={nombre}
                      onChange={(e) => setNombre(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="register-nombre-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-fecha-nacimiento" className="text-white">Fecha de Nacimiento</Label>
                    <Input
                      id="register-fecha-nacimiento"
                      type="date"
                      value={fechaNacimiento}
                      onChange={(e) => setFechaNacimiento(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="register-fecha-nacimiento-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-white">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-white">Contraseña</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-gray-900 border-gray-700 text-white"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-foto" className="text-white">Foto de Perfil (opcional)</Label>
                    <Input
                      id="register-foto"
                      type="file"
                      accept="image/*"
                      className="bg-gray-900 border-gray-700 text-white hidden"
                      data-testid="register-foto-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-gray-600"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => handleFileChange(e);
                          input.click();
                        }}
                      >
                        📁 Galería
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 border-gray-600"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.capture = 'environment';
                          input.onchange = (e) => handleFileChange(e);
                          input.click();
                        }}
                      >
                        📷 Cámara
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400">
                      📸 Toma una foto o selecciona desde tu galería
                    </p>
                  </div>
                  
                  {previewUrl && (
                    <div className="space-y-2">
                      <Label className="text-white">Vista Previa</Label>
                      <div className="flex justify-center">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="w-32 h-32 object-cover rounded-full border-4 border-gold"
                        />
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <div className="text-red-500 text-sm" data-testid="error-message">{error}</div>
                  )}
                  <Button
                    type="submit"
                    className="w-full btn-primary-mk"
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    {loading ? 'Cargando...' : 'Registrarse'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default Login;